import { formatPhoneDisplay, phoneToE164 } from 'shared';
import type { Address, Customer } from '@/lib/widget-types.js';

export interface CustomerFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street1: string;
  city: string;
  state: string;
  postalCode: string;
}

export type CustomerFieldErrors = Partial<Record<keyof CustomerFormState, string>>;

export type AddressFormState = Pick<CustomerFormState, 'street1' | 'city' | 'state' | 'postalCode'>;
export type AddressFieldErrors = Partial<Record<keyof AddressFormState, string>>;

export const emptyAddressForm: AddressFormState = {
  street1: '',
  city: '',
  state: '',
  postalCode: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POSTAL_PATTERN = /^\d{5}(-\d{4})?$/;

export function initialCustomerForm(existing: Customer | null): CustomerFormState {
  if (existing === null) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      street1: '',
      city: '',
      state: '',
      postalCode: '',
    };
  }
  return {
    firstName: existing.firstName,
    lastName: existing.lastName,
    email: existing.email,
    // Stored phone is E.164; the field shows the display format. Blank stays blank.
    phone: existing.phone === '' ? '' : formatPhoneDisplay(existing.phone),
    street1: existing.address.street1,
    city: existing.address.city,
    state: existing.address.state,
    postalCode: existing.address.postalCode,
  };
}

/** Split out so the entry screen and `customer-info` can't disagree on what a valid one is. */
export function validateAddressForm(form: AddressFormState): AddressFieldErrors {
  const errors: AddressFieldErrors = {};
  if (form.street1.trim().length === 0) errors.street1 = 'Enter your street address.';
  if (form.city.trim().length === 0) errors.city = 'Enter your city.';
  if (form.state.length !== 2) errors.state = 'Select your state.';
  if (!POSTAL_PATTERN.test(form.postalCode.trim())) errors.postalCode = 'Enter a valid ZIP code.';
  return errors;
}

/** Pure: returns field errors plus the assembled Customer, or `customer: null`. */
export function validateCustomerForm(form: CustomerFormState): {
  errors: CustomerFieldErrors;
  customer: Customer | null;
} {
  const errors: CustomerFieldErrors = { ...validateAddressForm(form) };

  if (form.firstName.trim().length === 0) errors.firstName = 'Enter your first name.';
  if (form.lastName.trim().length === 0) errors.lastName = 'Enter your last name.';
  if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = "That doesn't look like a complete email address.";
  }

  const phoneE164 = phoneToE164(form.phone);
  if (phoneE164 === null) errors.phone = 'Enter a US phone number (10 digits).';

  if (Object.keys(errors).length > 0 || phoneE164 === null) {
    return { errors, customer: null };
  }

  const address: Address = {
    street1: form.street1.trim(),
    city: form.city.trim(),
    state: form.state,
    postalCode: form.postalCode.trim(),
    country: 'US',
  };
  return {
    errors,
    customer: {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: phoneE164,
      address,
    },
  };
}
